import type { ISerialize } from '@/objects/ISerialize';
import { EngineDetails } from './EngineDetails';
import { formatDateIso } from '@/utilities/dateutils';

export class EngineEvent implements ISerialize {
    public ActiveFrom: Date;

    public ActiveTo: Date;

    public EngineDetails: EngineDetails | null;

    public EventID: number;

    public EventName: string | null;

    public LastLoaded: Date;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ActiveFrom?: Date;
        ActiveTo?: Date;
        EngineDetails?: EngineDetails | null;
        EventID?: number;
        EventName?: string | null;
        LastLoaded?: Date;
    }) {
        this.ActiveFrom = values.ActiveFrom ?? new Date('1970-01-01T00:00:00.000');

        this.ActiveTo = values.ActiveTo ?? new Date('1970-01-01T00:00:00.000');

        this.EngineDetails = values.EngineDetails ?? null;

        this.EventID = values.EventID ?? 0;

        this.EventName = values.EventName ?? '';

        this.LastLoaded = values.LastLoaded ?? new Date('1970-01-01T00:00:00.000');
    }

    public serialize(): string {
        return (
            '{' +
            `"ActiveFrom": ${'"' + formatDateIso(this.ActiveFrom) + '"'}` +
            ',' +
            `"ActiveTo": ${'"' + formatDateIso(this.ActiveTo) + '"'}` +
            ',' +
            `"EngineDetails": ${!this.EngineDetails ? null : this.EngineDetails?.serialize()}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"EventName": ${JSON.stringify(this.EventName)}` +
            ',' +
            `"LastLoaded": ${'"' + formatDateIso(this.LastLoaded) + '"'}` +
            '}'
        );
    }

    /**
     * Creates an instance of EngineEvent from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ActiveFrom?: Date;
        ActiveTo?: Date;
        EngineDetails?: EngineDetails | null;
        EventID?: number;
        EventName?: string | null;
        LastLoaded?: Date;
    }): EngineEvent {
        return new EngineEvent(values);
    }

    public static _FromServerJson(json: any): EngineEvent {
        return new EngineEvent({
            ActiveFrom: json.ActiveFrom instanceof Date ? json.ActiveFrom : new Date(json.ActiveFrom),
            ActiveTo: json.ActiveTo instanceof Date ? json.ActiveTo : new Date(json.ActiveTo),
            EngineDetails: json.EngineDetails ? EngineDetails._FromServerJson(json.EngineDetails) : null,
            EventID: json.EventID,
            EventName: json.EventName ?? '',
            LastLoaded: json.LastLoaded instanceof Date ? json.LastLoaded : new Date(json.LastLoaded),
        });
    }

    /**
     * Creates an instance of EngineEvent from another instance of EngineEvent.
     * @param other The EngineEvent you want to clone
     * @returns A cloned EngineEvent
     */
    public static FromOther(other: EngineEvent): EngineEvent {
        return EngineEvent._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return EngineEvent.FromOther(this);
    }

    static NAME_OF_CLASS = 'EngineEvent';
}
